import { decodeJWT } from '@sonar-software/sonar-ts-lib';
import { all, call, put, take } from 'redux-saga/effects';
import {
	AMPLITUDE_EVENT,
	amplitudeActions,
	amplitudeRedux,
	churnZeroActions,
	churnZeroRedux,
	fullstoryActions,
	fullstoryRedux,
	pendoActions,
	pendoRedux
} from '@sonar-software/sonar-analytics';
import { AsyncState, combineAllWatchers, ResultsCollection } from '@sonar-software/xuder';
import {
	convertCustomerToPendoAccount,
	convertDataToFullstoryData,
	convertUserToAmplitudeCustomerData,
	convertUserToPendoVisitor
} from './lib';
import { coreActions, Customer, OrganizationMetadata, User, UserReference } from '@sonar-software/sonar-core';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* initializeAmplitudeCustomer(
	currentUser: User,
	currentCustomer: Customer,
	organizationCount: number,
	userCount: number
) {
	const data = convertUserToAmplitudeCustomerData(currentUser, currentCustomer, organizationCount, userCount);

	yield put(amplitudeActions.requestInitializeAmplitude(data));
	yield put(amplitudeActions.requestAmplitudeEvent({ id: AMPLITUDE_EVENT.LOGIN_EVENT }));
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* initializeFullstoryCustomer(currentUser: User, currentCustomer: Customer) {
	const data = convertDataToFullstoryData(currentUser, currentCustomer);

	yield put(fullstoryActions.requestInitializeFullstory(data));
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* initializePendoCustomer(currentUser: User, currentCustomer: Customer) {
	const { shard } = decodeJWT();
	const visitor = convertUserToPendoVisitor(currentUser, shard);
	const account = convertCustomerToPendoAccount(currentCustomer, shard);

	yield put(pendoActions.requestInitializePendo(account, visitor));
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* initializeChurnZeroCustomer(currentUser: User, currentCustomer: Customer) {
	yield put(
		churnZeroActions.requestInitializeChurnZero({
			externalAccountId: currentCustomer.salesforceAccountId ?? '',
			externalContactId: currentUser.email
		})
	);
}

const receiveCurrentUserAction = coreActions.receiveGetCoreUsersCurrent({} as User);
const receiveCurrentCustomerAction = coreActions.receiveGetCoreCustomersCurrent({} as Customer);
const receiveOrganizationsAction = coreActions.receiveGetCoreOrganizationsAll({ results: [] });
const receiveUsersAction = coreActions.receiveGetCoreUsersAll({ results: [] });

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function* initializeAnalyticsWatcherSaga() {
	const initDependencies: {
		[0]: AsyncState<User>;
		[1]: AsyncState<Customer>;
		[2]: AsyncState<ResultsCollection<OrganizationMetadata>>;
		[3]: AsyncState<Array<UserReference>>;
	} = yield all([
		take(receiveCurrentUserAction.type),
		take(receiveCurrentCustomerAction.type),
		take(receiveOrganizationsAction.type),
		take(receiveUsersAction.type)
	]);

	const currentUser = initDependencies[ 0 ]?.data as User;
	const currentCustomer = initDependencies[ 1 ]?.data as Customer;
	const allOrganizations = initDependencies[ 2 ]?.data?.results as Array<OrganizationMetadata>;
	const allUsers = initDependencies[ 3 ].data as Array<UserReference>;

	const validOrganizations = allOrganizations.filter((org) => org.hasSalesforceData);

	yield call(initializeAmplitudeCustomer, currentUser, currentCustomer, validOrganizations.length, allUsers.length);
	yield call(initializeFullstoryCustomer, currentUser, currentCustomer);
	yield call(initializePendoCustomer, currentUser, currentCustomer);
	yield call(initializeChurnZeroCustomer, currentUser, currentCustomer);
}

export const analyticWatchers = combineAllWatchers([
	amplitudeRedux.amplitudeWatchers,
	churnZeroRedux.churnZeroWatchers,
	fullstoryRedux.fullstoryWatchers,
	pendoRedux.pendoWatchers,
	initializeAnalyticsWatcherSaga
]);
